import React, { useEffect, useMemo, useState } from 'react';
import { Application } from '../../types/application';
import {
  DialogContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useFetchUser } from '../../hooks/user-hooks';
import {
  useFetchCustomerApplications,
  useAddCustomerApplicationMutation,
  useUpdateCustomerApplicationMutation,
  useDeleteCustomerApplicationMutation,
} from '../../hooks/customer-hooks';
import StbSimpleTable from '../security-toolbox-ui/StbSimpleTable';
import StbButton from '../security-toolbox-ui/StbButton';
import StbModal from '../security-toolbox-ui/StbModal';
import { Controller, useForm } from 'react-hook-form';
import StbApiModal from '../security-toolbox-ui/StbApiModal';
import { HbrInput, HbrOption, HbrSelect } from '@harbor/elements-react-standalone';
import { renderErrorMessage } from '../security-toolbox-ui/security-toolbox-ui-utils';


type ModalAction = 'view' | 'update' | 'add';
const environmentOptions = ['Development', 'Staging', 'Production'];
const statusOptions = ['Planned', 'Development', 'Procurement', 'Operational', 'Retired'];
enum AppType {
  SaaS = 'SaaS',
  CloudNative = 'Cloud Native',
  CloudHosted = 'Cloud Hosted',
  Hybrid = 'Hybrid',
  OnPremises = 'On-premises',
}

interface CustomerApplicationsFormProps {
  customerId: string;
}

const CustomerApplicationsForm: React.FC<CustomerApplicationsFormProps> = ({ customerId }) => {
  const { data: user } = useFetchUser();

  const initialValues: Application = {
    id: null,
    name: '',
    type: '',
    description: '',
    last_updated: '',
    status: '',
    environment: '',
    updated_by: '',
    customer: customerId,
    cloud_hosting_details: [],
    on_premises_hosting_details: [],
    saas_application_details: [],
    compliance: [],
  };

  const { control, handleSubmit, reset, formState, watch } = useForm<Application>({
    defaultValues: initialValues,
    mode: 'onChange',
  });
  const applicationType = watch('type');


  const [showApplicationTypeModal, setShowApplicationTypeModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showFormType, setShowFormType] = useState<ModalAction>('view');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [applicationToDelete, setApplicationToDelete] = useState<number | null>(null);
  const [selectedApplicationType, setSelectedApplicationType] = useState<string | null>(null);
  const [applicationCounts, setApplicationCounts] = useState<{ [key: string]: number }>({});

  const { data: applicationData, error, isLoading } = useFetchCustomerApplications({ customer: customerId }, { enabled: !!customerId });
  const applications = useMemo(() => applicationData ?? [], [applicationData]);

  const { mutateAsync: addApplication } = useAddCustomerApplicationMutation();
  const { mutateAsync: updateApplication } = useUpdateCustomerApplicationMutation();
  const { mutateAsync: deleteApplication } = useDeleteCustomerApplicationMutation();

  useEffect(() => {
    if (!applications) return;
    const applicationTypes = Object.values(AppType) as string[];
    const counts = Object.values(AppType).reduce((acc, type) => ({ ...acc, [type]: 0 }), {});
    applications.forEach(app => {
      if (app.type && applicationTypes.includes(app.type)) {
        counts[app.type]++;
      }
    });
    setApplicationCounts(counts);
  }, [applicationData, applications]);

  const updateApplicationHandler = async (updatedApplication: Application) => {
    await updateApplication(updatedApplication)
  };

  const addApplicationHandler = async (newApplication: Application) => {
    await addApplication(newApplication);
  }

  const deleteApplicationHandler = async (applicationId: number) => {
    await deleteApplication({ customer: customerId as string, id: applicationId });
  }

  const handleSaveClick = async (data) => {
    if (showFormType === 'view') return;

    const payload = {
      ...data,
      updated_by: user.email,
      last_updated: new Date().toISOString(),
    };
    if (showFormType === 'update') {
      await updateApplicationHandler(payload);
    } else {
      await addApplicationHandler(payload);
    }
  };

  const handleEditIconClick = (application: Application) => {
    reset(application);
    setShowFormType('view');
    setShowModal(true);
  };

  const handleAddNewClick = () => {
    reset(initialValues);
    setShowModal(true);
    setShowFormType('add');
  };

  const handleApplicationTypeClick = (type: string) => {
    setSelectedApplicationType(type);
    setShowApplicationTypeModal(true);
  };

  const handleDeleteIconClick = (applicationId: number) => {
    setApplicationToDelete(applicationId);
    setShowDeleteModal(true);
  };

  const filteredApplications = applications.filter(app => selectedApplicationType ? app.type === selectedApplicationType : true);

  return (
    <div>
      <div className="flex justify-between items-center ">
        <h3>Applications</h3>
        <StbButton role="add" size="xsmall" onClick={handleAddNewClick} />
      </div>
      <StbSimpleTable
        loading={false}
        columns={['Type', 'Count']}
        data={Object.entries(applicationCounts).reduce((acc, [type, count]) => {
          acc[type] = [type, count.toString()];
          return acc;
        }, {})}
        onRowClick={(type) => handleApplicationTypeClick(type)}
        noHeader={true}
      />

      <StbModal
        open={showApplicationTypeModal}
        title={`Details for ${selectedApplicationType}`}
        onClose={async () => setShowApplicationTypeModal(false)}
        size="x-large"
        role="information"
      >
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Environment</TableCell>
                <TableCell>Updated By</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredApplications.length > 0 ? (
                filteredApplications.map((application) => (
                  <TableRow key={application.id}>
                    <TableCell>{application.name}</TableCell>
                    <TableCell>{application.type}</TableCell>
                    <TableCell>{application.last_updated}</TableCell>
                    <TableCell>{application.status}</TableCell>
                    <TableCell>{application.environment}</TableCell>
                    <TableCell>{application.updated_by}</TableCell>
                    <TableCell className="flex flex-row">
                      <StbButton role="update" size="small" variant="text" iconOnly className="mr-2" onClick={() => handleEditIconClick(application)} />
                      <StbButton role="delete" size="small" variant="text" iconOnly onClick={() => handleDeleteIconClick(application.id as number)} />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" align="center">No applications found for this type.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </StbModal>

      <StbApiModal
        open={showModal}
        title={showFormType === 'view' ? 'View Application' : showFormType === 'update' ? 'Edit Application' : 'Add New Application'}
        role={showFormType === 'view' ? 'update' : showFormType === 'update' ? 'update' : 'save'}
        hideCancel={false}
        onClose={async () => {
          setShowModal(false);
        }}
        onSubmit={async () => {
          if (showFormType === 'view') {
            setShowFormType('update')
          } else {
            handleSubmit(handleSaveClick)();
          }
        }}
        submitDisabled={showFormType !== 'view' && !formState.isValid}
        submitSuccess={`Customer application ${showFormType === 'update' ? 'updated' : 'created'} successfully`}
        submitError={`Error ${showFormType === 'update' ? 'updating' : 'creating'} customer application`}
        suppressResponses={showFormType === 'view'}
        suppressCloseOnSubmit={showFormType === 'view'}
      >
        <form 
          style={{
            '--hbr-color-neutral-disabled': 'var(--hbr-color-neutral-weak)',
            '--hbr-color-neutral-text-disabled': 'var(--hbr-color-neutral-text)',
            '--base-text-disabled': 'var(--hbr-color-neutral-text)',
            '--control-icon-in-disabled': 'var(--hbr-color-neutral-text)',
            '--hbr-control-color-disabled': 'var(--hbr-color-neutral-text)'
          } as React.CSSProperties}
        >
          <Controller
            name="name"
            control={control}
            rules={{
              validate: (value) => value && value.trim() !== '' ? true : 'Name is required'
            }}
            render={({ field }) => (
              <HbrInput
                label="Name"
                error={!!formState.errors['name']}
                value={field.value}
                onHbr-input={e => field.onChange(e.target.value)}
                disabled={showFormType === 'view'}
              >
                <div slot="label">Name {showFormType !== 'view' ? <em className="text-gray-500">(required)</em> : null}</div>
                {renderErrorMessage(!!formState.errors['name'], formState.errors['name']?.message)}
              </HbrInput>
            )}
          />

          <Controller
            name="type"
            control={control}
            rules={{
              validate: (value) => value ? true : 'Type is required'
            }}
            render={({ field }) => (
              <HbrSelect
                label="Type"
                value={field.value}
                disabled={showFormType === 'view'}
                onHbr-change={e => field.onChange(e.target.value)}
              >
                {Object.values(AppType).map((type, index) => (
                  <HbrOption key={index} value={type}>
                    {type}
                  </HbrOption>
                ))}
                <div slot="label">Type {showFormType !== 'view' ? <em className="text-gray-500">(required)</em> : null}</div>
              </HbrSelect>
            )}
          />

          {applicationType !== 'SaaS' && (
            <Controller
              name="version"
              control={control}
              render={({ field }) => (
                <HbrInput
                  label="Version"
                  value={field.value}
                  onHbr-change={e => field.onChange(e.target.value)}
                  disabled={showFormType === 'view'}
                />
              )}
            />
          )}

          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <HbrSelect
                label="Status"
                value={field.value}
                disabled={showFormType === 'view'}
                onHbr-change={e => field.onChange(e.target.value)}
              >
                {statusOptions.map((status, index) => (
                  <HbrOption key={index} value={status}>
                    {status}
                  </HbrOption>
                ))}
              </HbrSelect>
            )}
          />

          <Controller
            name="environment"
            control={control}
            render={({ field }) => (
              <HbrSelect
                label="Environment"
                value={field.value}
                disabled={showFormType === 'view'}
                onHbr-change={e => field.onChange(e.target.value)}
              >
                {environmentOptions.map((env, index) => (
                  <HbrOption key={index} value={env}>
                    {env}
                  </HbrOption>
                ))}
              </HbrSelect>
            )}
          />
        </form>
      </StbApiModal>

      <StbApiModal
        open={showDeleteModal}
        role="delete"
        title="Confirm Delete"
        onSubmit={() => deleteApplicationHandler(applicationToDelete)}
        submitSuccess="Application deleted successfully"
        submitError="Error deleting application"
        onClose={async () => setShowDeleteModal(false)}
      >
        Are you sure you want to delete this application?
      </StbApiModal>
    </div>
  );
};

export default CustomerApplicationsForm;